// Stage 1a — AE Details review page (FR-1.11–1.17 / US-1a.1–1a.8). Opened from a preprint card's
// "Details" action. Renders the pitch deck in two clearly-labelled halves: the student-authored
// CLAIM half (AE sanity-checks) and the reviewer-profile half the AE is responsible for verifying.
// Partial edits save without clobbering unedited fields; refine + status changes update live (SSE).
import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  MODEL_REVIEWERS_STRUCTURED_KEY,
  PAPER_COI_KEY,
  paperCoiAnswer,
  type ModelReviewerFields,
  type PaperCoiAnswer,
  type PreprintView,
  type RefereeView,
  type SelectedStatus,
  type SelectedView,
} from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { useAuth } from "../../lib/auth.js";
import { useCan } from "../../components/Can.js";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Spinner,
  StarRating,
  Textarea,
  statusTone,
} from "../../components/ui.js";
import { ProposeReviewer } from "./ProposeReviewer.js";
import { confirmAdvance } from "./advance-confirms.js";
import { useFeatures } from "../../hooks/useFeatures.js";
import { RichText } from "../../components/RichText.js";

// Deck field descriptors — the `key` is the verbatim `Selected.pitch` key (the field contract).
type FieldKind = "textarea" | "text" | "stars";
interface DeckField {
  key: string;
  kind: FieldKind;
  /** Display label when the raw pitch key reads poorly (defaults to the key). */
  label?: string;
}

// R-011: the SoftR four pitch criteria — stars + a short note each, persisted under
// `selected.pitch` with these keys (stars as "1".."5" strings like the claim ratings; the note
// under `<key> Note`). Key names are the stable field contract — document any change in the WO.
const PITCH_CRITERIA = [
  { key: "Priority(novelty)", label: "Novelty" },
  { key: "Priority(importance)", label: "Importance" },
  { key: "Priority(urgency)", label: "Urgency" },
  { key: "Priority(utility)", label: "Utility" },
] as const;
const criteriaNoteKey = (key: string): string => `${key} Note`;
const CRITERIA_FIELDS: DeckField[] = PITCH_CRITERIA.flatMap((c) => [
  { key: c.key, kind: "stars" as const, label: `${c.label} (stars)` },
  { key: criteriaNoteKey(c.key), kind: "textarea" as const, label: `${c.label} — short note` },
]);
const CLAIM_FIELDS: DeckField[] = [
  { key: "Research Q1", kind: "textarea" },
  { key: "Main Claim 1", kind: "textarea" },
  { key: "Claim 1 Rating", kind: "stars" },
  { key: "Claim 1 Rating Confidence", kind: "stars" },
  { key: "Research Q2", kind: "textarea" },
  { key: "Main Claim 2", kind: "textarea" },
  { key: "Claim 2 Rating", kind: "stars" },
  { key: "Claim 2 Rating Confidence", kind: "stars" },
  { key: "Research Q3", kind: "textarea" },
  { key: "Main Claim 3", kind: "textarea" },
  { key: "Claim 3 Rating", kind: "stars" },
  { key: "Claim 3 Rating Confidence", kind: "stars" },
  { key: "Strength of Evidence", kind: "stars" },
  { key: "Potential Limitation", kind: "textarea" },
  { key: "Method Used 1", kind: "text" },
  { key: "Method Used 2", kind: "text" },
  { key: "Method Used 3", kind: "text" },
  { key: "Reason(value-add)", kind: "textarea" },
  { key: "Reason(impact/importance)", kind: "textarea" },
  { key: "Reason(urgency)", kind: "textarea" },
  // R-011: the four SoftR criteria are filled here and presented on the pitch view.
  ...CRITERIA_FIELDS,
];
// WO-7/21 Track E: the reviewer-profile wording Angel dictated [11:34–12:25] — used verbatim in
// edit mode. Presentation: discipline + topical are said aloud; method expertise is Context-only
// (not presented), alongside Method Used 1–3.
const REVIEWER_PROFILE_FIELDS = [
  { key: "Reviewer Profile(discipline)", label: "What training / education should a reviewer have? (e.g. epidemiology)" },
  { key: "Reviewer Profile(method expertise)", label: "Please describe the minimum reviewer profile (describe the type of research they conduct)" },
  { key: "Reviewer Profile(topical expertise)", label: "Please list additional desired (but not required) reviewer characteristics (methods and/or research topics)" },
] as const;
/** Profile prompts spoken at the pitch (method expertise stays under Context). */
const PRESENTED_PROFILE_FIELDS = [REVIEWER_PROFILE_FIELDS[0], REVIEWER_PROFILE_FIELDS[2]] as const;
const PROFILE_TEXT_FIELDS: DeckField[] = [
  { key: REVIEWER_PROFILE_FIELDS[0].key, kind: "text", label: REVIEWER_PROFILE_FIELDS[0].label },
  { key: REVIEWER_PROFILE_FIELDS[1].key, kind: "textarea", label: REVIEWER_PROFILE_FIELDS[1].label },
  { key: REVIEWER_PROFILE_FIELDS[2].key, kind: "text", label: REVIEWER_PROFILE_FIELDS[2].label },
];
// Editable in edit mode as raw "Name — link" strings (they were only editable in the retired
// Pitch Deck popup — FR-K3 consolidated all deck editing here); presentation mode renders the
// parsed <ModelReviewers> cards instead.
const MODEL_REVIEWER_FIELDS: DeckField[] = [
  { key: "Peer Reviewer Model #1", kind: "text" },
  { key: "Peer Reviewer Model #2", kind: "text" },
];

const REFINE_TONE: Record<string, "slate" | "blue" | "green" | "red"> = {
  None: "slate",
  Created: "blue",
  Refined: "green",
};
const refineTone = (s: string | null): "slate" | "blue" | "green" | "red" =>
  s == null ? "slate" : (REFINE_TONE[s] ?? (s.startsWith("Failed") ? "red" : "slate"));

export function PreprintDetails(): JSX.Element {
  const { id = "" } = useParams();
  const { data: s, isLoading, isError } = useQuery({
    queryKey: qk.selectedOne(id),
    queryFn: () => api.get<SelectedView>(`/preprints/selected/${id}`),
    enabled: !!id,
  });

  if (isLoading) return <Spinner label="Loading preprint…" />;
  if (isError || !s) return <EmptyState>Preprint not found. <Link className="text-brand hover:underline" to="/preprints">Back to list</Link></EmptyState>;
  return <DetailsView s={s} />;
}

function DetailsView({ s }: { s: SelectedView }): JSX.Element {
  const qc = useQueryClient();
  const can = useCan();
  const { user } = useAuth();
  const { features } = useFeatures();
  const own = !!user && !!s.assigneeEmail && s.assigneeEmail.toLowerCase() === user.email.toLowerCase();
  const ctx = { domain: s.domain };
  // July-8 FR-N1: an AE edits BOTH halves of ANY preprint regardless of domain. `advanceToPitch
  // Editorial` with no domain scope is exactly the AE-and-above test (students and QC-in-training
  // are denied it outright), so it doubles as the "am I an AE?" tell — see FR-L8's cross-domain
  // advance. The server re-checks this on every write (deckScope in routes/preprints.ts).
  const isAe = can("advanceToPitchEditorial", {});
  const canEdit = own || isAe || can("editPitchDeck", ctx);
  const canProfile = isAe || can("editSimilarArticles", ctx); // profile half + refine

  // FR-P8: propose gate mirrors the server (managers anywhere; students Active or mini-team).
  const { data: pipeline } = useQuery({
    queryKey: qk.preprintOne(s.preprintId ?? ""),
    queryFn: () => api.get<PreprintView>(`/preprints/${s.preprintId}`),
    enabled: !!s.preprintId,
  });
  const me = user?.email.toLowerCase();
  const isMiniMember = !!me && (pipeline?.miniTeamEmails ?? []).some((e) => e.toLowerCase() === me);
  const statusProposable =
    isAe || pipeline?.status === "ACTIVE" || (pipeline?.status === "DORMANT" && isMiniMember);
  // ACTIVE is proposable cross-domain (WO-7/21 Track H); other statuses stay domain-scoped.
  const proposeCtx =
    pipeline?.status === "ACTIVE"
      ? {
          preprint: pipeline
            ? { miniTeamEmails: pipeline.miniTeamEmails, assignedEditorEmail: pipeline.assignedEditorEmail }
            : undefined,
        }
      : {
          domain: s.domain,
          preprint: pipeline
            ? { miniTeamEmails: pipeline.miniTeamEmails, assignedEditorEmail: pipeline.assignedEditorEmail }
            : undefined,
        };
  const canPropose = statusProposable && (isAe || can("claimEvaluateSuggestion", proposeCtx));

  // Presentation mode (FR-J1): a pitched deck opens read-only (whole-screen, screen-share
  // friendly, split-screen per FR-J7); editing is a separate mode entered deliberately.
  const pitched = s.status !== "Unread" && s.status !== "Taken";
  const [editMode, setEditMode] = useState(!pitched);

  // Draft = only the fields the AE has touched, so Save sends a minimal partial merge (FR-1.16).
  const [draft, setDraft] = useState<Record<string, string>>({});
  const dirtyKeys = Object.keys(draft);
  const dirty = dirtyKeys.length > 0;
  const valueOf = (key: string): string =>
    key in draft ? draft[key]! : String((s.pitch as Record<string, unknown>)[key] ?? "");
  const setField = (key: string, value: string): void => setDraft((d) => ({ ...d, [key]: value }));

  const save = useMutation({
    mutationFn: () => {
      const pitch: Record<string, string> = {};
      for (const k of dirtyKeys) pitch[k] = draft[k] ?? "";
      return api.patch<SelectedView>(`/preprints/selected/${s.id}`, { pitch });
    },
    onSuccess: (updated) => {
      qc.setQueryData(qk.selectedOne(s.id), updated);
      qc.invalidateQueries({ queryKey: ["selected"] });
      setDraft({});
    },
  });

  const isUrgent = valueOf("Urgent") === "Urgent";
  const setUrgent = useMutation({
    mutationFn: (urgent: boolean) => api.post(`/preprints/selected/${s.id}/urgent`, { urgent }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["selected"] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm">
        <Link className="text-brand hover:underline" to="/preprints">← Preprints</Link>
        {/* WO B3 entry point: the article-details reviewer/COI surface (FEATURE_COI-gated). */}
        {features.coi.enabled && s.preprintId && (
          <Link className="ml-auto text-brand hover:underline" to={`/preprints/${s.preprintId}/reviewers`}>
            Reviewers &amp; COI →
          </Link>
        )}
      </div>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={statusTone(s.status)}>{s.status}</Badge>
            {s.domain && <Badge tone="blue">{s.domain}</Badge>}
            {s.gatesFunded && <Badge tone="amber">Gates</Badge>}
            {isUrgent && <Badge tone="red">Urgent</Badge>}
            <span className="text-xs text-slate-500">
              Refinement: <Badge tone={refineTone(s.proposalStatus)}>{s.proposalStatus ?? "None"}</Badge>
            </span>
          </div>
          <h1 className="mt-1 text-xl font-bold">{s.title}</h1>
          <p className="text-xs text-slate-500">
            {s.assigneeEmail ? `Assigned: ${s.assigneeEmail}` : "Unassigned"}
            {s.doi && (
              <>
                {" · "}
                <a className="text-brand hover:underline" href={s.doi} target="_blank" rel="noreferrer">Open DOI</a>
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(canEdit || canProfile) && (
            <Button variant="secondary" onClick={() => setEditMode((m) => !m)} aria-pressed={editMode}>
              {editMode ? "Presentation view" : "Edit deck"}
            </Button>
          )}
          {editMode && canEdit && (
            <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
              {save.isPending ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </Button>
          )}
        </div>
      </header>

      {/* FR-P11: Presentation mode is a split panel (Presented vs muted Context) — no bracket-based
          hiding. Edit mode keeps Claims / Reviewer Profile halves so every field is fillable. */}
      {!editMode ? (
        <PresentationSplit s={s} />
      ) : (
      /* Split-screen (FR-J7): Claims left, Reviewer Profile right. */
      <div className="grid gap-4 lg:grid-cols-2">
        {/* ── Claims ─────────────────────────────────────────────────── */}
        <Card>
          <CardHeader className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold">Claims</h2>
              <p className="text-xs text-slate-500">Summary of the science — AE sanity-checks.</p>
            </div>
            {editMode && (
              <Button
                variant="ghost"
                onClick={() => setUrgent.mutate(!isUrgent)}
                disabled={!canEdit || setUrgent.isPending}
                aria-pressed={isUrgent}
              >
                {isUrgent ? "Unset urgent" : "Set urgent"}
              </Button>
            )}
          </CardHeader>
          <CardBody className="space-y-3">
            {CLAIM_FIELDS.map((f) => (
              <DeckFieldInput
                key={f.key}
                field={f}
                value={valueOf(f.key)}
                onChange={(v) => setField(f.key, v)}
                readOnly={!canEdit}
                present={!editMode}
              />
            ))}
          </CardBody>
        </Card>

        {/* ── Reviewer Profile ───────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold">Reviewer Profile</h2>
            <p className="text-xs text-slate-500">
              Drives the Stage 2 reviewer search — drafted on claim, verified and finalized by the AE.
            </p>
          </CardHeader>
          <CardBody className="space-y-4">
            {PROFILE_TEXT_FIELDS.map((f) => (
              <DeckFieldInput
                key={f.key}
                field={f}
                value={valueOf(f.key)}
                onChange={(v) => setField(f.key, v)}
                readOnly={!canProfile && !canEdit}
                present={!editMode}
              />
            ))}

            {/* WO-7/21 Track F: Model Peer Reviewers are the AE's half now — the student's deck
                stops before them [21:04–21:20]; the AE pulls two Prophy-suggested reviewers in
                after Refine Concepts. Presentation still shows the parsed cards to everyone. */}
            {editMode ? (
              canProfile ? (
                <>
                  {MODEL_REVIEWER_FIELDS.map((f) => (
                    <DeckFieldInput
                      key={f.key}
                      field={f}
                      value={valueOf(f.key)}
                      onChange={(v) => setField(f.key, v)}
                      readOnly={false}
                    />
                  ))}
                  {/* FR-P9: capture the full proposal-ready info so each model reviewer is
                      auto-proposed when the preprint is Selected. */}
                  <ModelReviewerStructuredEditor s={s} readOnly={false} />
                </>
              ) : (
                <p className="rounded-md border border-slate-100 bg-slate-50 px-2 py-1.5 text-xs text-slate-500">
                  Model Peer Reviewers are filled by your AE — after Refine Concepts they pick two
                  Prophy-suggested reviewers. Your part of the deck ends here.
                </p>
              )
            ) : (
              <ModelReviewers reviewers={s.modelReviewers} />
            )}

            {/* FR-K6: Nearest Neighbors inline, expanded — "they play a big part". */}
            <NearestNeighborPapers raw={String((s.pitch as Record<string, unknown>)["Nearest Neighbor Papers"] ?? "")} />
          </CardBody>
        </Card>
      </div>
      )}

      {/* ── Refine + Finalize gate, then the status controls (FR-N2/N3) ─ */}
      <ChangeStatus s={s} own={own} canProfile={canProfile} />

      {/* ── Embedded Propose a Reviewer (COI-first; hidden in presentation mode) ─ */}
      {editMode && (
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold">Propose a Reviewer</h2>
            <p className="text-xs text-slate-500">Runs the COI checks first; a passing reviewer is complete on submit.</p>
          </CardHeader>
          <CardBody>
            <ProposeReviewer selectedId={s.id} canPropose={canPropose} />
            {!statusProposable && !isAe && (
              <p className="mt-2 text-xs text-amber-700">
                You can propose reviewers once this preprint is Active
                {pipeline?.status === "DORMANT" ? " (or join its mini-team)" : ""}.
              </p>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function DeckFieldInput({
  field,
  value,
  onChange,
  readOnly,
  present = false,
}: {
  field: DeckField;
  value: string;
  onChange: (v: string) => void;
  readOnly: boolean;
  /** Presentation mode (FR-J1): clean read-only text, no edit chrome. */
  present?: boolean;
}): JSX.Element {
  const label = field.label ?? field.key;
  if (present) {
    return (
      <div>
        <span className="text-xs font-medium text-slate-600">{label}</span>
        {field.kind === "stars" ? (
          <StarRating value={Number.parseInt(value, 10) || 0} readOnly label={label} />
        ) : (
          <RichText>{value}</RichText>
        )}
      </div>
    );
  }
  return (
    <Field label={label}>
      {field.kind === "stars" ? (
        <StarRating value={Number.parseInt(value, 10) || 0} onChange={(n) => onChange(String(n))} readOnly={readOnly} label={label} />
      ) : field.kind === "textarea" ? (
        <Textarea value={value} onChange={(e) => onChange(e.target.value)} readOnly={readOnly} />
      ) : (
        <Input value={value} onChange={(e) => onChange(e.target.value)} readOnly={readOnly} />
      )}
    </Field>
  );
}

function ModelReviewers({ reviewers }: { reviewers: SelectedView["modelReviewers"] }): JSX.Element {
  return (
    <div>
      <h3 className="text-xs font-semibold text-slate-600">Model Peer Reviewers</h3>
      {reviewers.length === 0 ? (
        <p className="text-xs text-slate-400">None provided.</p>
      ) : (
        <ul className="mt-1 space-y-2">
          {reviewers.map((r) => (
            <li key={r.slot} className="rounded-md border border-slate-200 p-2">
              <div className="flex items-center gap-2">
                <Badge tone="slate">#{r.slot}</Badge>
                <span className="text-sm font-medium">{r.name || `Model #${r.slot}`}</span>
                {r.profileUrl && (
                  <a className="text-xs text-brand hover:underline" href={r.profileUrl} target="_blank" rel="noreferrer">Profile ↗</a>
                )}
                {r.pubmedUrl && (
                  <a className="text-xs text-brand hover:underline" href={r.pubmedUrl} target="_blank" rel="noreferrer">PubMed ↗</a>
                )}
              </div>
              {/* FR-P9: full proposal-ready info inline (auto-proposed when the preprint is Selected). */}
              {(r.email || r.affiliation || r.subdiscipline || r.bestMatchPaperTitle) && (
                <div className="mt-1 text-xs text-slate-500">
                  {[r.email, r.affiliation, r.subdiscipline && `sub-discipline: ${r.subdiscipline}`, r.bestMatchPaperTitle && `best-match: “${r.bestMatchPaperTitle}”`]
                    .filter(Boolean)
                    .join(" · ")}
                  {r.bestMatchUrl && (
                    <>
                      {" · "}
                      <a className="text-brand hover:underline" href={r.bestMatchUrl}>
                        best-match link
                      </a>
                    </>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── FR-P9: structured model-reviewer editor — the full proposal-ready fields ─
// Saves the per-slot array to the pitch key so the fields survive round-trip and drive auto-propose
// at Selected. Two slots (matching the legacy `Peer Reviewer Model #1/#2`).
const MODEL_SLOTS = [1, 2];

function ModelReviewerStructuredEditor({ s, readOnly }: { s: SelectedView; readOnly: boolean }): JSX.Element {
  const qc = useQueryClient();
  const seed = (): ModelReviewerFields[] =>
    MODEL_SLOTS.map((slot) => {
      const m = s.modelReviewers.find((r) => r.slot === slot);
      return {
        slot,
        name: m?.name ?? "",
        email: m?.email ?? "",
        affiliation: m?.affiliation ?? "",
        secondaryAffiliation: m?.secondaryAffiliation ?? "",
        highestDegree: m?.highestDegree ?? "",
        otherDegree: m?.otherDegree ?? "",
        title: m?.titleRole ?? "",
        otherTitles: m?.otherTitles ?? "",
        subdiscipline: m?.subdiscipline ?? "",
        bestMatchPaperTitle: m?.bestMatchPaperTitle ?? "",
        bestMatchUrl: m?.bestMatchUrl ?? "",
        profileUrl: m?.profileUrl ?? "",
        pubmedUrl: m?.pubmedUrl ?? "",
      };
    });
  const [rows, setRows] = useState<ModelReviewerFields[]>(seed);
  const set = (slot: number, key: keyof ModelReviewerFields, v: string): void =>
    setRows((rs) => rs.map((r) => (r.slot === slot ? { ...r, [key]: v } : r)));
  // WO-7/21 Track F: the AE pulls Prophy-suggested reviewers (from Refine Concepts) into the
  // slots — forcing model reviewers to come from Prophy, not memory [21:57–22:25].
  const { data: prophy } = useQuery({
    queryKey: qk.referees(s.preprintId ?? "no-preprint"),
    queryFn: () => api.get<RefereeView[]>(`/referees?preprintId=${s.preprintId}&status=Unclaimed`),
    enabled: !!s.preprintId && !readOnly,
  });
  const fillFromProphy = (slot: number, r: RefereeView): void =>
    setRows((rs) =>
      rs.map((row) =>
        row.slot === slot
          ? {
              ...row,
              name: r.name,
              affiliation: r.affiliations ?? row.affiliation,
              pubmedUrl: r.pubMedLink ?? row.pubmedUrl,
            }
          : row,
      ),
    );
  const save = useMutation({
    mutationFn: () =>
      api.patch<SelectedView>(`/preprints/selected/${s.id}`, {
        // Drop entirely-empty slots so we don't persist blanks.
        pitch: {
          [MODEL_REVIEWERS_STRUCTURED_KEY]: rows.filter((r) => r.name?.trim() || r.email?.trim()),
        },
      }),
    onSuccess: (updated) => {
      qc.setQueryData(qk.selectedOne(s.id), updated);
      qc.invalidateQueries({ queryKey: ["selected"] });
    },
  });

  return (
    <div className="space-y-3 rounded-md border border-slate-100 p-2">
      <h3 className="text-xs font-semibold text-slate-600">
        Model reviewer details — AE-owned, sourced from Prophy (auto-proposed when Selected)
      </h3>
      {/* Track F: Prophy suggestions from the last Refine Concepts run. */}
      {!readOnly && (
        <div className="rounded-md border border-blue-100 bg-blue-50/50 p-2">
          <p className="text-xs font-semibold text-slate-600">Prophy suggestions (from Refine Concepts)</p>
          {!s.preprintId || (prophy ?? []).length === 0 ? (
            <p className="mt-1 text-xs text-slate-500">
              No Prophy candidates yet — run <em>Refine Concepts / Updated Reviewers</em> below, then pick two from here.
            </p>
          ) : (
            <ul className="mt-1 space-y-1">
              {(prophy ?? []).slice(0, 6).map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-2 text-xs">
                  <span className="min-w-0 truncate">
                    <span className="font-medium">{r.name}</span>
                    <span className="ml-1 text-slate-500">{r.affiliations ?? "—"} · H {r.hIndex ?? "?"}</span>
                  </span>
                  <span className="flex shrink-0 gap-1">
                    {MODEL_SLOTS.map((slot) => (
                      <Button key={slot} variant="ghost" className="py-0.5 text-xs" onClick={() => fillFromProphy(slot, r)}>
                        → #{slot}
                      </Button>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {rows.map((r) => (
        <div key={r.slot} className="space-y-1.5 rounded-md border border-slate-200 p-2">
          <div className="text-xs font-semibold text-slate-500">Model #{r.slot}</div>
          {/* R-013 + WO-7/21 Track G: the exact submission-portal field set — identity,
              affiliations, degrees/titles, expertise phrasing, best-match title + URL, links. */}
          <div className="grid grid-cols-2 gap-1.5">
            <Input placeholder="Name" value={r.name ?? ""} onChange={(e) => set(r.slot, "name", e.target.value)} readOnly={readOnly} />
            <Input placeholder="Email" type="email" value={r.email ?? ""} onChange={(e) => set(r.slot, "email", e.target.value)} readOnly={readOnly} />
            <Input placeholder="University / affiliation" value={r.affiliation ?? ""} onChange={(e) => set(r.slot, "affiliation", e.target.value)} readOnly={readOnly} />
            <Input placeholder="Secondary affiliation (required)" value={r.secondaryAffiliation ?? ""} onChange={(e) => set(r.slot, "secondaryAffiliation", e.target.value)} readOnly={readOnly} />
            <Input placeholder="Highest degree (required)" value={r.highestDegree ?? ""} onChange={(e) => set(r.slot, "highestDegree", e.target.value)} readOnly={readOnly} />
            <Input placeholder="Other Degrees" value={r.otherDegree ?? ""} onChange={(e) => set(r.slot, "otherDegree", e.target.value)} readOnly={readOnly} />
            <Input placeholder="Title (required)" value={r.title ?? ""} onChange={(e) => set(r.slot, "title", e.target.value)} readOnly={readOnly} />
            <Input placeholder="Other titles" value={r.otherTitles ?? ""} onChange={(e) => set(r.slot, "otherTitles", e.target.value)} readOnly={readOnly} />
            <Input className="col-span-2" placeholder="Because of your expertise in… (required)" value={r.subdiscipline ?? ""} onChange={(e) => set(r.slot, "subdiscipline", e.target.value)} readOnly={readOnly} />
            <Input className="col-span-2" placeholder="Best-match paper title (required)" value={r.bestMatchPaperTitle ?? ""} onChange={(e) => set(r.slot, "bestMatchPaperTitle", e.target.value)} readOnly={readOnly} />
            <Input className="col-span-2" type="url" placeholder="Best-match paper URL or DOI (required)" value={r.bestMatchUrl ?? ""} onChange={(e) => set(r.slot, "bestMatchUrl", e.target.value)} readOnly={readOnly} />
            <Input placeholder="Institutional profile URL (required)" value={r.profileUrl ?? ""} onChange={(e) => set(r.slot, "profileUrl", e.target.value)} readOnly={readOnly} />
            <Input placeholder="PubMed / publications URL (required)" value={r.pubmedUrl ?? ""} onChange={(e) => set(r.slot, "pubmedUrl", e.target.value)} readOnly={readOnly} />
            {(r.subdiscipline?.trim() || r.bestMatchPaperTitle?.trim()) && (
              <p className="col-span-2 rounded-md border border-blue-100 bg-blue-50 px-2 py-1.5 text-xs italic text-blue-950">
                Justification preview: Because of your expertise in {r.subdiscipline?.trim() || "…"}, and especially your
                paper "{r.bestMatchPaperTitle?.trim() || "…"}", we think you'd be a terrific reviewer for this preprint.
              </p>
            )}
          </div>
        </div>
      ))}
      {!readOnly && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500">Name + email auto-propose; add profile fields, sub-discipline, and best-match to land it proposal-ready.</p>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save model reviewers"}
          </Button>
        </div>
      )}
    </div>
  );
}

// ── FR-P11: Presentation view — split panel (Presented vs muted Context) ─────
// WO-7/21 Track E: the presented deck follows Angel's meeting script [14:29–15:36] — each
// research question with its main claim AND that claim's star rating right beside it (no
// cross-panel scrolling, [09:40–10:15]), then potential limitations, the contribution-to-
// literature line, the pitch ratings (novelty/urgency + the four criteria), discipline +
// topical profile prompts, and model reviewers. Context keeps the abstract, strength of
// evidence, Method Used 1–3, and method expertise (not presented).
const CLAIM_PAIRS = [1, 2, 3].map((n) => ({
  rq: { key: `Research Q${n}`, label: `Research Question ${n}` },
  claim: { key: `Main Claim ${n}`, label: `Main Claim ${n}` },
  rating: `Claim ${n} Rating`,
  confidence: `Claim ${n} Rating Confidence`,
}));
const PRESENTED_TAIL: { key: string; label: string }[] = [
  { key: "Potential Limitation", label: "Potential Limitations" },
  { key: "Reason(value-add)", label: "What does this preprint contribute beyond existing literature?" },
];
const CONTEXT_TEXT: { key: string; label: string; stars?: boolean }[] = [
  { key: "Abstract", label: "Abstract" },
  { key: "Strength of Evidence", label: "Strength of Evidence", stars: true },
  { key: "Method Used 1", label: "Methods used" },
  { key: "Method Used 2", label: "Methods used (2)" },
  { key: "Method Used 3", label: "Methods used (3)" },
  {
    key: "Reviewer Profile(method expertise)",
    label: "Please describe the minimum reviewer profile (describe the type of research they conduct)",
  },
];

function PresentationSplit({ s }: { s: SelectedView }): JSX.Element {
  const pitch = s.pitch as Record<string, unknown>;
  const val = (key: string): string => String(pitch[key] ?? "");
  const num = (key: string): number => Number.parseInt(val(key), 10) || 0;
  const abstract = val("Abstract") || s.abstractBlurb || "";
  return (
    <div className="grid gap-4 lg:grid-cols-[1.35fr_1fr]">
      {/* Presented panel */}
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-brand">▶ Presented — say this at the pitch</h2>
        </CardHeader>
        <CardBody className="space-y-5">
          {/* Track E: RQ + claim + that claim's stars together, so both sides track while scrolling. */}
          {CLAIM_PAIRS.filter((c) => val(c.rq.key).trim() || val(c.claim.key).trim()).map((c) => (
            <div key={c.rq.key} className="space-y-2 rounded-md border border-slate-100 p-2.5">
              {val(c.rq.key).trim() && (
                <div className="space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{c.rq.label}</span>
                  <RichText>{val(c.rq.key)}</RichText>
                </div>
              )}
              {val(c.claim.key).trim() && (
                <div className="space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{c.claim.label}</span>
                  <RichText>{val(c.claim.key)}</RichText>
                </div>
              )}
              {(num(c.rating) > 0 || num(c.confidence) > 0) && (
                <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5">
                    Rating <StarRating value={num(c.rating)} readOnly label={`${c.claim.label} rating`} />
                  </span>
                  {num(c.confidence) > 0 && (
                    <span className="flex items-center gap-1.5">
                      Confidence <StarRating value={num(c.confidence)} readOnly label={`${c.claim.label} confidence`} />
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
          {PRESENTED_TAIL.filter((f) => val(f.key).trim()).map((f) => (
            <div key={f.key} className="space-y-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{f.label}</span>
              <RichText>{val(f.key)}</RichText>
            </div>
          ))}
          {/* Stars are spoken; short notes sit to their right and are marked not presented. */}
          <PitchCriteria pitch={pitch} />
          {/* Track E: discipline + topical are spoken; method expertise is Context-only. */}
          {PRESENTED_PROFILE_FIELDS.filter((f) => val(f.key).trim()).map((f) => (
            <div key={f.key} className="space-y-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{f.label}</span>
              <RichText>{val(f.key)}</RichText>
            </div>
          ))}
          <div className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Model peer reviewers</span>
            <ModelReviewers reviewers={s.modelReviewers} />
          </div>
        </CardBody>
      </Card>

      {/* Context panel — muted, "not presented" */}
      <Card className="border-dashed bg-slate-50">
        <CardHeader className="border-dashed">
          <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-400">
            Context — not presented <Badge tone="slate">muted</Badge>
          </h2>
          <p className="text-xs text-slate-400">Visible to the AE &amp; editorial for reference; not read aloud.</p>
        </CardHeader>
        <CardBody className="space-y-4 text-slate-500">
          {abstract.trim() && (
            <div className="space-y-1">
              <span className="text-xs font-semibold uppercase tracking-wide">Abstract</span>
              <RichText className="text-slate-500">{abstract}</RichText>
            </div>
          )}
          {CONTEXT_TEXT.filter((f) => f.key !== "Abstract" && val(f.key).trim()).map((f) => (
            <div key={f.key} className="space-y-1">
              <span className="text-xs font-semibold uppercase tracking-wide">
                {f.label} <span className="text-[10px] font-medium uppercase text-slate-400">not presented</span>
              </span>
              {f.stars ? (
                <StarRating value={Number.parseInt(val(f.key), 10) || 0} readOnly label={f.label} />
              ) : (
                <RichText className="text-slate-500">{val(f.key)}</RichText>
              )}
            </div>
          ))}
        </CardBody>
      </Card>
    </div>
  );
}

/** R-011: four SoftR criteria — stars on the Presented path; short notes sit to the right
 *  and are tagged not-presented (AE/editorial reference only). */
function PitchCriteria({ pitch }: { pitch: Record<string, unknown> }): JSX.Element {
  const val = (k: string): string => String(pitch[k] ?? "");
  const rows = PITCH_CRITERIA.map((c) => ({
    label: c.label,
    stars: Number.parseInt(val(c.key), 10) || 0,
    note: val(criteriaNoteKey(c.key)).trim(),
  }));
  return (
    <div className="space-y-3">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Why this preprint — four criteria
      </span>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.label} className="flex flex-wrap items-start gap-3">
            <span className="w-24 shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-500">{r.label}</span>
            <StarRating value={r.stars} readOnly label={r.label} />
            <div className="min-w-0 flex-1 text-xs text-slate-400">
              <span className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-slate-400">
                Note · not presented
              </span>
              {r.note ? (
                <RichText className="text-slate-400">{r.note}</RichText>
              ) : (
                <span className="italic">—</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Inline + expanded by default (FR-K6): the team reads through these during pitches. */
function NearestNeighborPapers({ raw }: { raw: string }): JSX.Element | null {
  if (!raw.trim()) return null;
  return (
    <div className="text-xs">
      <h3 className="font-semibold text-slate-600">Nearest Neighbor Papers</h3>
      <RichText className="text-xs text-slate-600">{raw}</RichText>
    </div>
  );
}

// Change-Status gate — advance / select / reject, gated by can(...) and the same action mapping
// the API enforces. Selection is direct + AE-gated (July-2 FR-J4).
//
// July-8 FR-N2/FR-N3 turn the two AE gates into a visible, auditable checklist (the `ae-pitch-deck`
// mockup's "gate" bar): Refine Concepts must have run, and the AE must tick "I have reviewed &
// finalized the deck" — only then does Advance light up. The server enforces both (409 otherwise);
// disabling the button is a courtesy, not the control. Setting `Selected` still fires the "changes
// discussed in editorial?" confirm (FR-J5 semantics, retained by FR-N2).
const NEXT: Partial<Record<SelectedStatus, { status: SelectedStatus; label: string; action: string }>> = {
  Taken: { status: "To Pitch(Team)", label: "Ready to pitch (Team)", action: "setToPitchTeam" },
  "To Pitch(Team)": { status: "To Pitch(Editorial)", label: "Advance to Editorial", action: "advanceToPitchEditorial" },
  "To Pitch(Editorial)": { status: "Selected", label: "Set Selected", action: "setToSelected" },
};
const REJECT_TARGET: Partial<Record<SelectedStatus, SelectedStatus>> = {
  "To Pitch(Team)": "Team Rejected",
  "To Pitch(Editorial)": "Editorial Rejected",
};
/** The AE gates the FR-N2/N3 checklist guards. The student's own → To Pitch(Team) is not gated. */
const AE_GATES: SelectedStatus[] = ["To Pitch(Team)", "To Pitch(Editorial)"];

const fmtStamp = (iso: string): string => new Date(iso).toLocaleString();

function ChangeStatus({ s, own, canProfile }: { s: SelectedView; own: boolean; canProfile: boolean }): JSX.Element | null {
  const qc = useQueryClient();
  const can = useCan();
  const ctx = { domain: s.domain, beforePitching: s.status === "Taken" };
  const next = NEXT[s.status];
  const rejectTarget = REJECT_TARGET[s.status];
  const [reason, setReason] = useState("");

  const invalidate = (): void => {
    qc.invalidateQueries({ queryKey: ["selected"] });
    qc.invalidateQueries({ queryKey: qk.selectedOne(s.id) });
  };
  const advance = useMutation({
    mutationFn: (status: SelectedStatus) =>
      api.post(`/preprints/selected/${s.id}/advance`, { status, ...(reason ? { reason } : {}) }),
    onSuccess: invalidate,
  });
  const refine = useMutation({
    mutationFn: () => api.post(`/preprints/selected/${s.id}/refine`, { mode: "reviewers" }),
    onSuccess: invalidate,
  });
  const finalize = useMutation({
    mutationFn: (finalized: boolean) => api.post(`/preprints/selected/${s.id}/finalize`, { finalized }),
    onSuccess: invalidate,
  });
  // R-017: the paper-level COI answer, stored on the pitch (a gate answer — saving it does NOT
  // clear the FR-N2 finalization; the server exempts this key).
  const setPaperCoi = useMutation({
    mutationFn: (answer: PaperCoiAnswer) =>
      api.patch(`/preprints/selected/${s.id}`, { pitch: { [PAPER_COI_KEY]: answer } }),
    onSuccess: invalidate,
  });

  // July-8 FR-L8: AE-gated advances + gate rejects are cross-domain (no domain in the check).
  const canAdvance = !!next && (next.action === "setToPitchTeam" ? own || can("setToPitchTeam", ctx) : can(next.action as never, {}));
  const canReject = !!rejectTarget && can((next?.action ?? "advanceToPitchEditorial") as never, {});
  if (!next && !rejectTarget) return null;

  // ── The FR-N2/N3 gate state ──
  const atAeGate = AE_GATES.includes(s.status);
  const refining = s.proposalStatus === "Created";
  const refined = !!s.refinedAt;
  const finalized = !!s.finalizedAt;
  // FR-N3 blocks both AE gates; FR-N2's Finalize is specifically the door into Editorial.
  const needsFinalize = s.status === "To Pitch(Team)";
  // R-017: the paper-level COI question must be answered (yes OR no) before Editorial.
  const paperCoi = paperCoiAnswer(s.pitch as Record<string, unknown>);
  const blockers = atAeGate
    ? [
        !refined && "Run Refine Concepts first",
        needsFinalize && !finalized && "Finalize the deck first",
        needsFinalize && !paperCoi && "Answer the paper-level COI question first",
      ].filter(Boolean)
    : [];
  const gateBlocked = blockers.length > 0;

  return (
    <Card>
      <CardHeader>
        <h2 className="text-sm font-semibold">Change Status</h2>
        <p className="text-xs text-slate-500">
          {atAeGate
            ? "Refine and finalize before this deck can advance — both are recorded against your name."
            : "Record your gate decision."}
        </p>
      </CardHeader>

      {/* FR-K8: Refine sits on BOTH pitch gates ("sometimes I get this wrong, so I have to
          re-refine"). FR-N3: it is now a required gate, not just a button. */}
      {atAeGate && canProfile && (
        <CardBody className="flex flex-wrap items-center gap-3 border-b border-slate-100">
          <Button variant="secondary" onClick={() => refine.mutate()} disabled={refine.isPending || refining}>
            {refining ? "Refining…" : "↻ Refine Concepts / Updated Reviewers"}
          </Button>
          {refined ? (
            <Badge tone="green">✓ Refined {fmtStamp(s.refinedAt!)}</Badge>
          ) : (
            <Badge tone="amber">Required before advancing</Badge>
          )}
        </CardBody>
      )}

      {/* R-017: paper-level COI — a forced yes/no; the editorial advance refuses while unset. */}
      {atAeGate && canProfile && (
        <CardBody className="flex flex-wrap items-center gap-3 border-b border-slate-100">
          <span className="text-sm font-medium">Paper-level COI?</span>
          {(["yes", "no"] as const).map((answer) => (
            <label key={answer} className="flex items-center gap-1.5 text-sm">
              <input
                type="radio"
                name={`paper-coi-${s.id}`}
                checked={paperCoi === answer}
                disabled={setPaperCoi.isPending}
                onChange={() => setPaperCoi.mutate(answer)}
              />
              {answer === "yes" ? "Yes — conflict noted" : "No conflict"}
            </label>
          ))}
          {paperCoi ? (
            <Badge tone={paperCoi === "yes" ? "red" : "green"}>Paper COI: {paperCoi}</Badge>
          ) : (
            <Badge tone="amber">Required before Editorial</Badge>
          )}
        </CardBody>
      )}

      {/* FR-N2: the first-class Finalize control that replaces the pop-up-only gate. */}
      {atAeGate && canProfile && (
        <CardBody className="flex flex-wrap items-center gap-3 border-b border-slate-100">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={finalized}
              disabled={finalize.isPending}
              onChange={(e) => finalize.mutate(e.target.checked)}
            />
            I have reviewed &amp; finalized the deck
          </label>
          {finalized ? (
            <Badge tone="green">
              Finalized by {s.finalizedBy} · {fmtStamp(s.finalizedAt!)}
            </Badge>
          ) : (
            <Badge tone="amber">Required before Editorial</Badge>
          )}
        </CardBody>
      )}

      <CardBody className="flex flex-wrap items-center gap-2">
        {canAdvance && next && (
          <>
            <Button
              onClick={() => {
                // FR-N2 keeps the "changes discussed in editorial?" confirm on → Selected.
                if (!confirmAdvance(next.status)) return;
                advance.mutate(next.status);
              }}
              disabled={advance.isPending || gateBlocked}
              title={gateBlocked ? blockers.join(" · ") : undefined}
            >
              {next.label}
            </Button>
            {gateBlocked && <p className="text-xs text-amber-700">{blockers.join(" · ")}.</p>}
          </>
        )}
        {/* R-016: the Reject control leads; the optional reason follows it (SoftR order). */}
        {canReject && rejectTarget && (
          <>
            <Button
              variant="danger"
              onClick={() => {
                if (window.confirm(`Reject this preprint at the ${s.status} gate?`)) advance.mutate(rejectTarget);
              }}
              disabled={advance.isPending}
            >
              Reject
            </Button>
            <Input className="max-w-xs" placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
          </>
        )}
        {!canAdvance && !canReject && <p className="text-xs text-slate-500">No status changes available to you here.</p>}
      </CardBody>
    </Card>
  );
}
